import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import useFieldLabelsAPI from '../hooks/useFieldLabelsAPI';

/**
 * Hook to translate chart series names and axis labels.
 *
 * Series keys arrive as "cataloging_view_total_instances" (dots replaced with underscores)
 * — same format as GenericTableView column keys.
 *
 * Resolution chain (same as resolveLabel in table views):
 *   key → restore dot → fieldAliasMap → aliasMember → labelsMap → label ✅
 *
 * @param {object} resultSet
 * @param {string} reportType
 * @param {object} [fieldAliasMap] - { viewFieldName: aliasMember } from query
 */
export const useChartTranslations = (resultSet, reportType, fieldAliasMap = {}) => {
  const intl = useIntl();
  const { labelsMap: dimLabels } = useFieldLabelsAPI({ fieldType: 'dimension' });
  const { labelsMap: measLabels } = useFieldLabelsAPI({ fieldType: 'measure' });
  const labelsMap = { ...dimLabels, ...measLabels };

  const isRTL = intl.locale === 'ar' || intl.locale === 'ar-SA' || intl.locale === 'he';

  const translateLabel = useMemo(() => {
    return (label) => {
      if (!label) return label;

      // Try all dot-restore positions (same as resolveLabel in table views)
      const parts = label.split('_');
      for (let i = 1; i < parts.length; i++) {
        const candidate = parts.slice(0, i).join('_') + '.' + parts.slice(i).join('_');
        const alias = fieldAliasMap?.[candidate];
        if (alias && labelsMap?.[alias]) return labelsMap[alias];
        if (labelsMap?.[candidate]) return labelsMap[candidate];
      }

      // Direct lookup (for non-view keys)
      if (labelsMap?.[label]) return labelsMap[label];

      return label;
    };
  }, [labelsMap, fieldAliasMap]);

  return { translateLabel, effectiveReportType: reportType, isRTL };
};
